/**
 * KAN-756: API доски CRM. Контракт: types/crm.ts.
 * KAN-757: период доски и частичная правка строки клиента (ответ: строка доски).
 * KAN-862: канбан-доска (types/crmPipeline.ts): обзор, порции этапа, смена этапа.
 * KAN-865: строка одного клиента для панели карточки доски.
 * KAN-880: страница колонки доски (?column=) и таблица лидов GET /api/v1/crm/leads/.
 * KAN-883: "Лиды по дням" GET /api/v1/crm/stats/daily/.
 */
import apiClient from './client';
import type {
  CrmBoard,
  CrmBoardRange,
  CrmDailyStats,
  CrmDailyStatsParams,
  CrmLeadsPage,
  CrmLeadsParams,
  CrmRow,
  CrmRowPatch,
} from '@/types/crm';
import type {
  CrmClientDetail,
  CrmColumnCardsParams,
  CrmPipelineParams,
  CrmPipelineResponse,
  CrmStageCardsPage,
  CrmStageCardsParams,
  CrmStageId,
} from '@/types/crmPipeline';

const PIPELINE_URL = '/api/v1/crm/pipeline/';

export const crmApi = {
  /** KAN-757: период ?date_from=&date_to= (по Москве, включительно). */
  getBoard: async (range: CrmBoardRange): Promise<CrmBoard> =>
    (await apiClient.get<CrmBoard>('/api/v1/crm/board/', { params: range })).data,
  updateClient: async (clientId: string, patch: CrmRowPatch): Promise<CrmRow> =>
    (await apiClient.patch<CrmRow>(`/api/v1/crm/clients/${encodeURIComponent(clientId)}/`, patch)).data,
  /** KAN-862: обзор канбан-доски; active_days только когда задан. */
  getPipeline: async (params: CrmPipelineParams): Promise<CrmPipelineResponse> =>
    (await apiClient.get<CrmPipelineResponse>(PIPELINE_URL, { params })).data,
  /** KAN-862: порция карточек одного этапа (свёрнутый этап, "Загрузить ещё"). */
  getPipelineStage: async (params: CrmStageCardsParams): Promise<CrmStageCardsPage> =>
    (await apiClient.get<CrmStageCardsPage>(PIPELINE_URL, { params })).data,
  /** KAN-880: порция карточек колонки доски ?column=<key> с фильтрами доски. */
  getPipelineColumn: async (params: CrmColumnCardsParams): Promise<CrmStageCardsPage> =>
    (await apiClient.get<CrmStageCardsPage>(PIPELINE_URL, { params })).data,
  /** KAN-880: таблица лидов страницами (offset/limit) с фильтрами доски, атрибутом и организацией. */
  getLeads: async (params: CrmLeadsParams): Promise<CrmLeadsPage> =>
    (await apiClient.get<CrmLeadsPage>('/api/v1/crm/leads/', { params })).data,
  /** KAN-862: перенос клиента в этап (перетаскивание на доске, "Сменить этап"). */
  setClientStage: async (clientId: string, stageId: CrmStageId): Promise<CrmRow> =>
    (await apiClient.patch<CrmRow>(`/api/v1/crm/clients/${encodeURIComponent(clientId)}/`, { stage_id: stageId }))
      .data,
  /** KAN-865: строка CRM одного клиента с этапами и атрибутами (панель карточки доски). */
  getClientRow: async (clientId: string): Promise<CrmClientDetail> =>
    (await apiClient.get<CrmClientDetail>(`/api/v1/crm/clients/${encodeURIComponent(clientId)}/`)).data,
  /** KAN-883: показатели по дням периода (МСК, включительно) с фильтром менеджера. */
  getDailyStats: async (params: CrmDailyStatsParams): Promise<CrmDailyStats> =>
    (await apiClient.get<CrmDailyStats>('/api/v1/crm/stats/daily/', { params })).data,
};
